import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Form, Input, Modal, Row, Select } from 'antd';
import { useForm } from 'antd/es/form/Form';
import debounce from 'lodash/debounce';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { useQuery } from '@apollo/client';
import { GET_AREAS_LIST, GET_GOVERNORATES_LIST } from '../../graphql/queries';

import {
  EditLocation,
  saveSubLocations,
} from '../../redux/actions/initiatives';
import NewLocationMap from './NewLocationMap';

const { Option } = Select;

const EditLocationModal = ({ show, setShow, data, refetchData }) => {
  const [editLocationForm] = useForm();

  const Close = () => {
    setShow(false);
  };

  const { loading: governoratesListLoading, data: governoratesList } = useQuery(
    GET_GOVERNORATES_LIST,
  );

  const { loading: areasListLoading, data: areasList } =
    useQuery(GET_AREAS_LIST);

  const dispatch = useDispatch();

  const [errorMessage, setErrorMessage] = useState(null);
  const [isChecked] = useState(true);
  const [governorate, setGovernorate] = useState('');
  const [inputFields] = useState(() => {
    if (data?.node?.subLocations?.length > 0) {
      return data.node.subLocations.map(obj => ({
        id: obj.id,
        name_en: obj.nameEn || '',
        name_ar: obj.nameAr || '',
        governorate: obj.governorate || '',
        area: obj.area || '',
        point1: obj.point1 || '',
        point2: obj.point2 || '',
        point3: obj.point3 || '',
        point4: obj.point4 || '',
        radius: obj.radius || '',
        map_url: obj.mapUrl || '',
      }));
    }
    return [
      {
        name_en: '',
        name_ar: '',
        governorate: '',
        area: '',
        point1: '',
        point2: '',
        point3: '',
        point4: '',
        map_url: '',
        latitude: '',
        longitude: '',
        radius: '',
      },
    ];
  });

  const [locationFields, setlocationFields] = useState({
    lat: data?.node?.latitude,
    lng: data?.node?.longitude,
    radius: data?.node?.radius || 0,
  });

  const onFinish = async values => {
    const LocationId = data.node.pk;
    const FormData = {
      ...values,
      is_main_location: true,
      // set data based on the value of isChecked
      governorate: isChecked ? values.governorate : '',
      area: isChecked ? values.area : '',
      point1: isChecked ? values.point1 : '',
      point2: isChecked ? values.point2 : '',
      point3: isChecked ? values.point3 : '',
      point4: isChecked ? values.point4 : '',
      latitude: isChecked ? locationFields.lat : '',
      longitude: isChecked ? locationFields.lng : '',
      map_url: isChecked ? values.mapUrl : '',
      radius: locationFields.radius,
    };
    const response = await dispatch(EditLocation(LocationId, FormData));
    // if an error appeared while adding the main location.
    if (typeof response === 'string' && response.includes('Error'))
      setErrorMessage(response);
    // if no error proceed with adding the sub-locations.
    else {
      setErrorMessage(null);
      const subLocationData = {
        main_location: response.id,
        is_active: response.is_active,
        main_location_address: response.main_location_address,
      };
      if (response.main_location_address) {
        subLocationData['governorate'] = '';
        subLocationData['area'] = '';
        subLocationData['point1'] = '';
        subLocationData['point2'] = '';
        subLocationData['point3'] = '';
        subLocationData['point4'] = '';
        subLocationData['map_url'] = '';
      }
      await dispatch(saveSubLocations(inputFields, subLocationData));
      // close the modal and refetch data.
      setShow(false);
      refetchData();
      // reset form values.
      editLocationForm.resetFields();
    }
  };

  const debouncedSetRadius = debounce(value => {
    setlocationFields(prev => ({
      ...prev,
      radius: Number(value),
    }));
  }, 10);

  return (
    <Modal
      title="Edit Location Form"
      footer={null}
      open={show}
      onCancel={Close}
      width="1200px"
    >
      {isChecked && (
        <NewLocationMap
          apiKey={process.env.REACT_APP_TOMTOM_API_KEY}
          onLocationClick={({ lng, lat }) => {
            setlocationFields(prev => ({
              ...prev,
              lat,
              lng,
            }));
          }}
          initialLatitude={data?.node?.latitude}
          initialLongitude={data?.node?.longitude}
          radius={locationFields.radius}
        />
      )}
      <Form form={editLocationForm} onFinish={onFinish}>
        {errorMessage && <p className="text-error">{errorMessage}</p>}

        <Row gutter={[8, 8]}>
          <Col md={12} xs={24}>
            <Form.Item
              name="name_en"
              label="Name in English"
              initialValue={data.node.nameEn}
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col md={12} xs={24}>
            <Form.Item
              name="name_ar"
              label="Name in Arabic"
              initialValue={data.node.nameAr}
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input className="arabic-field" />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              name="is_active"
              label={
                <span>
                  Is Active&nbsp;&nbsp;
                  <InfoCircleOutlined
                    title="whether or not to show the location in the application form"
                    style={{
                      verticalAlign: 'middle',
                    }}
                  />
                </span>
              }
              valuePropName="checked"
              initialValue={data.node.isActive}
            >
              <Checkbox />
            </Form.Item>
          </Col>

          {/* <Col xs={24}>
            <Form.Item
              name="main_location_address"
              label="Add address information per main location"
              valuePropName="checked"
              initialValue={data.node.mainLocationAddress}
            >
              <Checkbox
                onChange={event => setIsChecked(event.target.checked)}
              />
            </Form.Item>
          </Col> */}

          {isChecked && (
            <>
              <Col md={12} xs={24}>
                <Form.Item
                  name="governorate"
                  label="Governorate"
                  initialValue={data.node.governorate}
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Select onChange={value => setGovernorate(value)}>
                    <Option key="" value="">
                      -----
                    </Option>
                    ,
                    {!governoratesListLoading &&
                      governoratesList?.governorates.edges.map(obj => (
                        <Option key={obj.node.id} value={obj.node.name}>
                          {obj.node.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col md={12} xs={24}>
                <Form.Item
                  name="area"
                  label="Area"
                  initialValue={data.node.area}
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Select>
                    <Option key="" value="">
                      -----
                    </Option>
                    ,
                    {governorate &&
                      !areasListLoading &&
                      areasList?.areas.edges
                        .filter(
                          obj => obj.node.governorate.name === governorate,
                        )
                        .map(obj => (
                          <Option key={obj.node.id} value={obj.node.name}>
                            {obj.node.name}
                          </Option>
                        ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col md={8} xs={24}>
                <Form.Item
                  label="Latitude"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Input
                    value={locationFields.lat}
                    onChange={e => {
                      setlocationFields({
                        ...locationFields,
                        lat: e.currentTarget.value,
                      });
                    }}
                  />
                </Form.Item>
              </Col>
              <Col md={8} xs={24}>
                <Form.Item
                  label="longitude"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Input
                    value={locationFields.lng}
                    onChange={e => {
                      setlocationFields({
                        ...locationFields,
                        lng: e.currentTarget.value,
                      });
                    }}
                  />
                </Form.Item>
              </Col>

              <Col md={8} xs={24}>
                <Form.Item
                  label="Radius (in meters)"
                  initialValue={data.node.radius}
                  rules={[{ required: true }]}
                >
                  <Input
                    value={locationFields.radius}
                    onChange={e => {
                      debouncedSetRadius(e.currentTarget.value);
                    }}
                  />
                </Form.Item>
              </Col>
            </>
          )}

          {/* <Col xs={24}>
            <Form.List name="sub-locations-form" initialValue={inputFields}>
              {(fields, { add, remove }) => {
                return (
                  <Card
                    title={
                      <>
                        Sub-Locations
                        <PlusCircleOutlined
                          className="mx-3"
                          onClick={() => {
                            addFields();
                            add();
                          }}
                        />
                      </>
                    }
                    className="mb-5"
                  >
                    {fields.map((field, index) => (
                      <React.Fragment key={index}>
                        <Row gutter={[8, 8]} key={field.key}>
                          <Col md={12} xs={24}>
                            <Form.Item
                              name={[index, 'name_en']}
                              label="Name in English"
                              rules={[
                                {
                                  required: true,
                                  message: "'name_en' is required",
                                },
                              ]}
                            >
                              <Input
                                name="name_en"
                                onChange={event =>
                                  onChange(
                                    index,
                                    event.target.name,
                                    event.target.value,
                                  )
                                }
                              />
                            </Form.Item>
                          </Col>

                          <Col md={12} xs={24}>
                            <Form.Item
                              name={[index, 'name_ar']}
                              label="Name in Arabic"
                              rules={[
                                {
                                  required: true,
                                  message: "'name_ar' is required",
                                },
                              ]}
                            >
                              <Input
                                className="arabic-field"
                                name="name_ar"
                                onChange={event =>
                                  onChange(
                                    index,
                                    event.target.name,
                                    event.target.value,
                                  )
                                }
                              />
                            </Form.Item>
                          </Col>

                          {!isChecked && (
                            <>
                              <Col md={12} xs={24}>
                                <Form.Item
                                  name={[index, 'governorate']}
                                  label="Governorate"
                                  initialValue=""
                                  rules={[
                                    {
                                      required: true,
                                      message: "'governorate' is required",
                                    },
                                  ]}
                                >
                                  <Select
                                    onChange={value =>
                                      onChange(index, 'governorate', value)
                                    }
                                  >
                                    <Option key="" value="">
                                      -----
                                    </Option>
                                    ,
                                    {!governoratesListLoading &&
                                      governoratesList?.governorates.edges.map(
                                        obj => (
                                          <Option
                                            key={obj.node.id}
                                            value={obj.node.name}
                                          >
                                            {obj.node.name}
                                          </Option>
                                        ),
                                      )}
                                  </Select>
                                </Form.Item>
                              </Col>

                              <Col md={12} xs={24}>
                                <Form.Item
                                  name={[index, 'area']}
                                  label="Area"
                                  initialValue=""
                                  rules={[
                                    {
                                      required: true,
                                      message: "'area' is required",
                                    },
                                  ]}
                                >
                                  <Select
                                    onChange={value =>
                                      onChange(index, 'area', value)
                                    }
                                  >
                                    <Option key="" value="">
                                      -----
                                    </Option>
                                    ,
                                    {inputFields[index]['governorate'] &&
                                      !areasListLoading &&
                                      areasList?.areas.edges
                                        .filter(
                                          obj =>
                                            obj.node.governorate.name ===
                                            inputFields[index]['governorate'],
                                        )
                                        .map(obj => (
                                          <Option
                                            key={obj.node.id}
                                            value={obj.node.name}
                                          >
                                            {obj.node.name}
                                          </Option>
                                        ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                            </>
                          )}

                          <Col md={4} xs={24}>
                            <Button
                              onClick={() => {
                                removeFields(index);
                                remove(field.name);
                              }}
                            >
                              Remove
                            </Button>
                          </Col>
                        </Row>

                        {fields.length > 1 && index !== fields.length - 1 && (
                          <Divider />
                        )}
                      </React.Fragment>
                    ))}
                  </Card>
                );
              }}
            </Form.List>
          </Col> */}

          <Col xs={24}>
            <Button htmlType="submit">Submit</Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default EditLocationModal;
